/* eslint-disable no-console */
import { ErrorTracker } from "./app/utils/errorHandler/errorTracker";

/**
 * Polyfills
 */
if (!Object["values"]) {
  Object["values"] = function(obj) {
    if (typeof obj !== "object" || obj === null) {
      throw new TypeError("Object.values called on non-object");
    }
    const values: any[] = [];
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        values.push(obj[key]);
      }
    }
    return values;
  };
}

// Temporary polyfill for the string.padStart function
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
if (!String.prototype.padStart) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    String.prototype.padStart = function (targetLength, padString) {
        // eslint-disable-next-line no-param-reassign
        targetLength = targetLength >> 0; //truncate if number, or convert non-number to 0;
        // eslint-disable-next-line no-param-reassign
        padString = String((typeof padString !== "undefined" ? padString : " "));
        if (this.length > targetLength) {
            return String(this);
        } else {
            // eslint-disable-next-line no-param-reassign
            targetLength = targetLength - this.length;
            if (targetLength > padString.length) {
                // eslint-disable-next-line no-param-reassign
                padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
            }
            return padString.slice(0, targetLength) + String(this);
        }
    };
}


// This file should be remove in favor of core-js when security approve the lib

// Polyfill for Array.prototype.flatMap for browsers that don't support it (Chrome < 69)
// @ts-expect-error - flatMap not available in older lib versions
if (!Array.prototype.flatMap) {
    // @ts-expect-error - flatMap not available in older lib versions
    Array.prototype.flatMap = function<T, U>(
        callback: (value: T, index: number, array: T[]) => U | U[],
        thisArg?: any
    ): U[] {
        return this.reduce((acc: U[], val: T, index: number, array: T[]) => {
            const result = callback.call(thisArg, val, index, array);
            return acc.concat(result);
        }, []);
    };
}

// Polyfill for Object.fromEntries which is not available in older browsers like Chrome 53
// This is needed by modern JavaScript libraries including React Router
// @ts-expect-error - fromEntries not available in older lib versions
if (!Object.fromEntries) {
    // @ts-expect-error - fromEntries not available in older lib versions
    Object.fromEntries = function<T = any>(entries: Iterable<readonly [PropertyKey, T]>): { [k: string]: T } {
        const obj: { [k: string]: T } = {};
        const entriesArray = Array.from(entries as any);
        for (let i = 0; i < entriesArray.length; i++) {
            const entry = entriesArray[i] as [PropertyKey, T];
            obj[String(entry[0])] = entry[1];
        }
        return obj;
    };
}

// Polyfill for queueMicrotask which is not available in older browsers like Chrome 53
// This is needed by React 19 and modern async handling
if (typeof queueMicrotask === "undefined") {
    window.queueMicrotask = function(callback: () => void): void {
        Promise.resolve().then(callback).catch((error) => {
            setTimeout(() => {
                throw error;
            }, 0);
        });
    };
}

// Polyfill for ResizeObserver which is not available in older browsers like Chrome 53
// This is needed by React 19 and modern UI components
if (typeof ResizeObserver === "undefined") {
    window.ResizeObserver = class ResizeObserver {
        private readonly callback: ResizeObserverCallback;
        private readonly observedElements: Set<Element> = new Set();
        private intervalId: number | null = null;

        constructor(callback: ResizeObserverCallback) {
            this.callback = callback;
        }

        observe(target: Element): void {
            this.observedElements.add(target);

            // Start polling if not already running
            if (!this.intervalId) {
                this.intervalId = window.setInterval(() => {
                    const entries: ResizeObserverEntry[] = [];
                    this.observedElements.forEach((element) => {
                        const rect = element.getBoundingClientRect();
                        entries.push({
                            target: element,
                            contentRect: rect,
                            borderBoxSize: [{
                                inlineSize: rect.width,
                                blockSize: rect.height
                            }],
                            contentBoxSize: [{
                                inlineSize: rect.width,
                                blockSize: rect.height
                            }],
                            devicePixelContentBoxSize: [{
                                inlineSize: rect.width,
                                blockSize: rect.height
                            }]
                        } as ResizeObserverEntry);
                    });

                    if (entries.length > 0) {
                        this.callback(entries, this);
                    }
                }, 100);
            }
        }

        unobserve(target: Element): void {
            this.observedElements.delete(target);

            // Stop polling if no elements are being observed
            if (this.observedElements.size === 0 && this.intervalId) {
                window.clearInterval(this.intervalId);
                this.intervalId = null;
            }
        }

        disconnect(): void {
            this.observedElements.clear();
            if (this.intervalId) {
                window.clearInterval(this.intervalId);
                this.intervalId = null;
            }
        }
    };
}

// Polyfill/wrapper for Performance API to handle React 19's performance profiling
// React 19 uses performance.mark and performance.measure internally, which can fail in older browsers
if (typeof performance !== "undefined") {
    const originalMark = performance.mark.bind(performance);
    const originalMeasure = performance.measure.bind(performance);
    const originalClearMarks = performance.clearMarks.bind(performance);
    const originalClearMeasures = performance.clearMeasures.bind(performance);

    // Wrap performance.mark to handle non-string mark names
    performance.mark = function(markName: any, markOptions?: any): PerformanceMark {
        try {
            // Ensure markName is always a string
            const name = typeof markName === "string" ? markName : String(markName);
            return originalMark(name, markOptions);
        } catch (e) {
            // Return a dummy PerformanceMark to avoid breaking React
            return {} as PerformanceMark;
        }
    };

    // Wrap performance.measure to handle missing marks and non-string names
    performance.measure = function(measureName: any, startOrMeasureOptions?: any, endMark?: any): PerformanceMeasure {
        try {
            // Ensure measureName is always a string
            const name = typeof measureName === "string" ? measureName : String(measureName);
            return originalMeasure(name, startOrMeasureOptions, endMark);
        } catch (e) {
            // Return a dummy PerformanceMeasure to avoid breaking React
            return {} as PerformanceMeasure;
        }
    };

    // Wrap clearMarks to handle errors
    performance.clearMarks = function(markName?: any): void {
        try {
            if (markName !== undefined) {
                const name = typeof markName === "string" ? markName : String(markName);
                originalClearMarks(name);
            } else {
                originalClearMarks();
            }
        } catch (e) {
            // Silently fail
        }
    };

    // Wrap clearMeasures to handle errors
    performance.clearMeasures = function(measureName?: any): void {
        try {
            if (measureName !== undefined) {
                const name = typeof measureName === "string" ? measureName : String(measureName);
                originalClearMeasures(name);
            } else {
                originalClearMeasures();
            }
        } catch (e) {
            // Silently fail
        }
    };
}

// Polyfill for Object.entries which is not available in older browsers like Chrome 53
// This is needed for error handling and modern JavaScript
// @ts-expect-error - entries not available in older lib versions
if (!Object.entries) {
    // @ts-expect-error - entries not available in older lib versions
    Object.entries = function<T>(obj: { [s: string]: T } | ArrayLike<T>): [string, T][] {
        const ownProps = Object.keys(obj);
        const result = new Array(ownProps.length);
        for (let i = 0; i < ownProps.length; i++) {
            result[i] = [ownProps[i], obj[ownProps[i]]];
        }
        return result;
    };
}

// Polyfill for Element.prepend and Element.append which are not available in Chrome 53
// These are needed by modern DOM manipulation
if (!Element.prototype.prepend) {
    (Element.prototype as any).prepend = function(...nodes: (Node | string)[]): void {
        const fragment = document.createDocumentFragment();
        nodes.forEach(node => {
            const nodeToInsert = typeof node === "string" ? document.createTextNode(node) : node;
            fragment.appendChild(nodeToInsert);
        });
        this.insertBefore(fragment, this.firstChild);
    };
}

if (!Element.prototype.append) {
    (Element.prototype as any).append = function(...nodes: (Node | string)[]): void {
        nodes.forEach(node => {
            const nodeToInsert = typeof node === "string" ? document.createTextNode(node) : node;
            this.appendChild(nodeToInsert);
        });
    };
}

// Also add prepend/append for DocumentFragment
if (!DocumentFragment.prototype.prepend) {
    (DocumentFragment.prototype as any).prepend = function(...nodes: (Node | string)[]): void {
        const fragment = document.createDocumentFragment();
        nodes.forEach(node => {
            const nodeToInsert = typeof node === "string" ? document.createTextNode(node) : node;
            fragment.appendChild(nodeToInsert);
        });
        this.insertBefore(fragment, this.firstChild);
    };
}

if (!DocumentFragment.prototype.append) {
    (DocumentFragment.prototype as any).append = function(...nodes: (Node | string)[]): void {
        nodes.forEach(node => {
            const nodeToInsert = typeof node === "string" ? document.createTextNode(node) : node;
            this.appendChild(nodeToInsert);
        });
    };
}

// **END OF POLYFILL

/**
 * Default.ts
 * Responsible for minimal setup required for default.html and default-legacy.html
 * Even if launcher.pack.js fails, we want some essential pieces configured (e.g. Sentry error tracker, AppCache, polyfills)
 * The anonymous function's role is to prevent polluting the global 'window' namespace.
 */
(function (window) {
    let errorTracker = new ErrorTracker();
    errorTracker.initialize();
    window.errorTracker = errorTracker;

    // Update last-server for cordova Drive application
    let notifyShellLastServer = function () {
        if (self !== top) {
            let loc = window.location;
            window.parent.postMessage({ type: "drive:last-server", server: `${ loc.hostname }${ loc.port ? `:${ loc.port }` : "" }` }, "file:///");
        }
    };

    // Override geotab.require because RequireJS takes care of it
    // Still need to invoke the method because RJS doesn't know about that
    try {
        geotab.declare("geotab.addin");
        geotab["require"] = function (_, method) {
            (method)();
        };
    } catch (err) {
        if (window.confirm("There was a network issue when trying to load the app.\n\nTry again?")) {
            window.location.reload();
        }
    }

    let updateAppCache = () => {
        try {
            window.applicationCache.update();
        } catch (e) {
            if (e.code === DOMException.INVALID_STATE_ERR || e.name === "InvalidStateError") {
                // Browser purged the appcache. Trigger it to download again by reloading the app.
                window.location.reload();
                return;
            }
            throw e;
        }
    };

    window.appcacheInitErrorHandler = function (evt) {
        console.log(`APPCACHE: Error: ${ evt.message }`);
        if (!window.serviceWorkerEnabled && window.applicationCache.status === window.applicationCache.UNCACHED) {
            if (self !== top) {
                window.parent.postMessage({ type: "drive:appcacheerror", message: evt.message }, "file:///*");
            } else {
                window.appcacheErrorHandlerTimer = setTimeout(() => {
                    if (window.applicationCache.status !== window.applicationCache.UNCACHED) {
                        updateAppCache();
                    }
                }, 10000);
            }
        } else {
            updateAppCache();
        }
    };

    // AppCache Handling
    // Makes sure we are completely cached when an update is available
    // (A workaround for when CheckmateServer occasionally gives a 404 on a file)
    if (window.applicationCache && !window.serviceWorkerEnabled) {
        window.applicationCache.addEventListener("updateready", function() {
            notifyShellLastServer();

            if (window.DRIVE_DEBUG && !window.DRIVE_DEBUG.DISABLE_APPCACHE_RELOAD) {
                window.location.reload();
            }
            console.log("APPCACHE: Updated successfully");
        }, false);

        window.applicationCache.addEventListener("cached", function() {
            notifyShellLastServer();
            console.log("APPCACHE: Successfully cached");
        }, false);

        window.applicationCache.addEventListener("noupdate", function() {
            notifyShellLastServer();
            console.log("APPCACHE: Checked and no update required");
        }, false);

        window.applicationCache.addEventListener("downloading", function() {
            (window as any).downloadStartTime = new Date().getTime();
            console.log("APPCACHE: Update found, downloading...");
        }, false);

        window.applicationCache.addEventListener("progress", function (evt) {
            if (evt && evt.loaded && evt.total) {
                console.log(`APPCACHE: ${ evt.loaded }/${ evt.total } loaded`);
            }
        });

        window.applicationCache.addEventListener("error", window.appcacheInitErrorHandler);
    }

    // Service Worker Handling
    if (window.serviceWorkerEnabled && "serviceWorker" in navigator) {
        void navigator.serviceWorker.ready.then(() => {
            notifyShellLastServer();
            console.log("SERVWORKER: Active");
        });
    }

    window.DRIVE_DEBUG = {
        DISABLE_APPCACHE_RELOAD: false,
        DISABLE_SERVICEWORKER_RELOAD: false
    };
})(window);